import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Media types
export const MediaTypes = {
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  OTHER: 'other'
};

// Media categories
export const MediaCategories = {
  SPORT: 'sport',
  NEWS: 'news',
  EVENT: 'event',
  GALLERY: 'gallery',
  DOCUMENT: 'document',
  OTHER: 'other'
};

class MediaService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/media`;
  }

  // Get authentication headers
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Get all media with filters and pagination
  async getAllMedia(params = {}) {
    try {
      const response = await axios.get(this.baseURL, {
        params,
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to fetch media');
    }
  }

  // Get single media item by ID
  async getMediaById(id) {
    try {
      const response = await axios.get(`${this.baseURL}/${id}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to fetch media');
    }
  }

  // Upload media files
  // Simplified upload method - always uses 'files' field name
  async uploadMedia(files, metadata = {}) {
    try {
      const formData = new FormData();
      
      // Always use 'files' field name for consistency
      if (Array.isArray(files)) {
        files.forEach(file => {
          formData.append('files', file);
        });
      } else {
        // Single file also uses 'files' field name
        formData.append('files', files);
      }

      // Add metadata with proper validation
      Object.keys(metadata).forEach(key => {
        const value = metadata[key];
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, String(value));
        }
      });

      const response = await axios.post(`${this.baseURL}/upload`, formData, {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {

      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to upload media';
      throw new Error(errorMessage);
    }
  }

  // Update media metadata
  async updateMedia(id, updates) {
    try {
      const response = await axios.put(`${this.baseURL}/${id}`, updates, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to update media');
    }
  }

  // Delete media
  async deleteMedia(id) {
    try {
      const response = await axios.delete(`${this.baseURL}/${id}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to delete media');
    }
  }

  // Get media statistics (admin only)
  async getMediaStats() {
    try {
      const response = await axios.get(`${this.baseURL}/admin/stats`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to fetch media statistics');
    }
  }

  // Get sport-specific media
  async getSportMedia(sportId, params = {}) {
    try {
      const response = await axios.get(this.baseURL, {
        params: { ...params, sportId },
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to fetch sport media');
    }
  }



  // Search media
  async searchMedia(searchTerm, params = {}) {
    try {
      const response = await axios.get(this.baseURL, {
        params: { ...params, search: searchTerm },
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to search media');
    }
  }

  // Filter media by type
  async getMediaByType(mediaType, params = {}) {
    try {
      const response = await axios.get(this.baseURL, {
        params: { ...params, mediaType },
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to fetch media by type');
    }
  }

  // Filter media by category
  async getMediaByCategory(category, params = {}) {
    try {
      const response = await axios.get(this.baseURL, {
        params: { ...params, category },
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to fetch media by category');
    }
  }

  // Get public media only
  async getPublicMedia(params = {}) {
    try {
      const response = await axios.get(this.baseURL, {
        params: { ...params, isPublic: true }
      });
      return response.data;
    } catch (error) {

      throw new Error(error.response?.data?.error || 'Failed to fetch public media');
    }
  }

  // Utility functions
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  isValidFileType(file, allowedTypes = []) {
    if (allowedTypes.length === 0) {
      // Default allowed types
      allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
        'video/mp4', 'video/mpeg', 'video/quicktime',
        'application/pdf'
      ];
    }
    return allowedTypes.includes(file.type);
  }

  getMaxFileSize() {
    return 10 * 1024 * 1024; // 10MB
  }

  validateFile(file) {
    const errors = [];
    
    if (!this.isValidFileType(file)) {
      errors.push(`File type ${file.type} is not allowed`);
    }
    
    if (file.size > this.getMaxFileSize()) {
      errors.push(`File size exceeds maximum limit of ${this.formatFileSize(this.getMaxFileSize())}`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  getMediaTypeFromFile(file) {
    if (file.type.startsWith('image/')) return MediaTypes.IMAGE;
    if (file.type.startsWith('video/')) return MediaTypes.VIDEO;
    if (file.type === 'application/pdf') return MediaTypes.DOCUMENT;
    return MediaTypes.OTHER;
  }
}

// Create and export a singleton instance
const mediaService = new MediaService();
export default mediaService;

// Export the class for testing or custom instances
export { MediaService };